import { useRef } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useLanguage } from "../context/LanguageContext";
import { Printer, Download, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

const Invoice = ({ isOpen, onClose, order, customer }) => {
  const { t, isRTL } = useLanguage();
  const invoiceRef = useRef(null);

  const handlePrint = () => {
    const printContent = invoiceRef.current;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  if (!order) return null;

  const subtotal = order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || order.total || 0;
  const tax = subtotal * 0; // No tax for now
  const grandTotal = subtotal + tax;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" dir={isRTL ? "rtl" : "ltr"}>
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="font-heading">{t("invoice")}</DialogTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" /> {t("printInvoice")}
            </Button>
          </div>
        </DialogHeader>

        <div ref={invoiceRef} className="p-8 bg-white" id="invoice-content">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8 border-b pb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Bahr Al Rimal" className="w-20 h-20 rounded-full object-cover" />
              <div>
                <h1 className="text-2xl font-bold text-[#1a365d]">
                  {isRTL ? "شركة بحر الرمال" : "Bahr Al Rimal"}
                </h1>
                <p className="text-sm text-gray-600">
                  {isRTL ? "لإستيراد الأدوية والمعدات الطبية" : "Medical Supplies Import & Distribution"}
                </p>
                <p className="text-xs text-gray-500">
                  {isRTL ? "طرابلس، ليبيا" : "Tripoli, Libya"}
                </p>
              </div>
            </div>
            <div className={`text-${isRTL ? 'left' : 'right'}`}>
              <h2 className="text-3xl font-bold text-[#d4af37]">{t("invoice")}</h2>
              <p className="text-sm text-gray-600 mt-2">
                <span className="font-semibold">{t("invoiceNumber")}:</span> INV-{order.id?.slice(0, 8).toUpperCase() || "N/A"}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{t("invoiceDate")}:</span> {new Date(order.created_at || Date.now()).toLocaleDateString(isRTL ? 'ar-LY' : 'en-US')}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div className="mb-8 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-semibold text-[#1a365d] mb-2">{t("billTo")}:</h3>
            <p className="font-medium">{customer?.full_name || order.customer_name || "Customer"}</p>
            <p className="text-sm text-gray-600">{customer?.phone || order.customer_phone || ""}</p>
            <p className="text-sm text-gray-600">{customer?.address || order.shipping_address || ""}</p>
          </div>

          {/* Items Table */}
          <table className="w-full mb-8">
            <thead>
              <tr className="bg-[#1a365d] text-white">
                <th className={`py-3 px-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t("itemDescription")}</th>
                <th className="py-3 px-4 text-center">{t("quantity")}</th>
                <th className="py-3 px-4 text-center">{t("unitPrice")}</th>
                <th className={`py-3 px-4 ${isRTL ? 'text-left' : 'text-right'}`}>{t("total")}</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, index) => (
                <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <td className={`py-3 px-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <p className="font-medium">{isRTL ? item.name_ar || item.name : item.name}</p>
                    {item.sku && <p className="text-xs text-gray-500">SKU: {item.sku}</p>}
                  </td>
                  <td className="py-3 px-4 text-center">{item.quantity}</td>
                  <td className="py-3 px-4 text-center">{(item.price || 0).toFixed(2)} {t("lyd")}</td>
                  <td className={`py-3 px-4 ${isRTL ? 'text-left' : 'text-right'}`}>
                    {((item.price || 0) * (item.quantity || 1)).toFixed(2)} {t("lyd")}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    {isRTL ? "لا توجد عناصر" : "No items"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
            <div className="w-64">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{t("subtotal")}:</span>
                <span className="font-medium">{subtotal.toFixed(2)} {t("lyd")}</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">{t("tax")} (0%):</span>
                  <span className="font-medium">{tax.toFixed(2)} {t("lyd")}</span>
                </div>
              )}
              <div className="flex justify-between py-3 bg-[#1a365d] text-white px-3 rounded mt-2">
                <span className="font-bold">{t("grandTotal")}:</span>
                <span className="font-bold text-lg">{grandTotal.toFixed(2)} {t("lyd")}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t text-center">
            <p className="text-[#d4af37] font-semibold mb-2">{t("thankYou")}</p>
            <div className="flex justify-center gap-8 text-sm text-gray-600">
              <span>📞 +218 91 234 5678</span>
              <span>📧 info@bahralrimal.ly</span>
              <span>🌐 www.bahralrimal.ly</span>
            </div>
          </div>

          {/* Watermark */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400">
              {isRTL ? "هذه الفاتورة تم إنشاؤها إلكترونياً" : "This invoice was generated electronically"}
            </p>
          </div>
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #invoice-content, #invoice-content * {
              visibility: visible;
            }
            #invoice-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default Invoice;
